// ============================================================================
//  csOpsApi.js — Frontend wrappers for /cs_ops_api.php
//
//  Mirrors the reportsApi.js style: each named export is a thin async wrapper
//  around axios.get / axios.post against the Cold Store Operations endpoint.
//
//  Auth: relies on the global axios interceptor in App.js to attach the
//  Bearer token from localStorage.getItem('token').
// ============================================================================

import axios from 'axios';
import { API_BASE_URL } from '../config';

const CS_OPS = `${API_BASE_URL}/cs_ops_api.php`;

// Fallback: ensure the Authorization header is present even if a future caller
// imports this module in isolation (matches the explicit pattern used by other
// dashboards, e.g. ExecutiveDashboard.js line 89-95).
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const call = async (action, params = {}) => {
  try {
    const res = await axios.get(CS_OPS, {
      params: { action, ...params },
      headers: { ...authHeaders() },
    });
    return res.data;
  } catch (err) {
    return {
      success: false,
      error: err.response?.data?.error || err.message || 'Network error',
    };
  }
};

// ----------------------------------------------------------------------------
//  Endpoints — matches cs_ops_api.php action router
// ----------------------------------------------------------------------------

// KPIs reflect stock & out-for-processing AS-OF a date.
// asOfDate omitted → defaults to today (server-side).
export const fetchCSOpsKpis = (asOfDate = '', shift = 'ALL') =>
  call('kpis', { as_of: asOfDate || todayISO(), shift });

export const fetchCSOpsInward = (date, shift = 'ALL') =>
  call('inward', { date, shift });

export const fetchCSOpsOutward = (date, shift = 'ALL') =>
  call('outward', { date, shift });

// Drilldown now uses as-of-stock semantics (current stock breakdown at end of $asOfDate).
export const fetchCSOpsDrilldown = (asOfDate = '', shift = 'ALL') =>
  call('drilldown', { as_of: asOfDate || todayISO(), shift });

export const fetchCSOpsTrend = (date, period = 'Week', shift = 'ALL') =>
  call('trend', { date, period, shift });

// Bin Occupancy — cs = 'ALL' | 'CS1' | 'CS2'
export const fetchCSOpsBinOccupancy = (asOfDate = '', cs = 'ALL') =>
  call('bin_occupancy', { as_of: asOfDate || todayISO(), cs });

// Stock Pallets — pallet-level detail for any hierarchy path
// path = { product, brand?, grade?, pack_size? }
// opts = { cs?: 'ALL' | 'CS1' | 'CS2', limit?: number }   defaults: ALL, 200
// Any property in path may be empty/undefined to wildcard that level.
export const fetchCSOpsStockPallets = (asOfDate = '', path = {}, opts = {}) =>
  call('stock_pallets', {
    as_of:     asOfDate || todayISO(),
    product:   path.product   || '',
    brand:     path.brand     || '',
    grade:     path.grade     || '',
    pack_size: path.pack_size || '',
    cs:        opts.cs        || 'ALL',
    ...(opts.limit ? { limit: opts.limit } : {}),
  });

// ----------------------------------------------------------------------------
//  Helpers
// ----------------------------------------------------------------------------

export const todayISO = () => new Date().toISOString().slice(0, 10);

export const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

export const prevDay = (iso) => {
  const d = new Date(iso);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};
